import { buildCanonicalAuthHeaders } from "./authenticatedRequest";

const inFlightRequests = new Map();
const responseCache = new Map();
const TENANT_CONTRACT_VERSION = "tenant-request.v1";
let activeTenantFingerprint = "";
let activeTenantFacts = {};
let tenantGeneration = 0;

const resolveHeaderValue = (headers, key) => {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    return String(headers.get(key) || "");
  }
  if (Array.isArray(headers)) {
    const match = headers.find(([k]) => String(k).toLowerCase() === String(key).toLowerCase());
    return match ? String(match[1] || "") : "";
  }
  if (typeof headers === "object") {
    const direct = headers[key];
    if (direct != null) return String(direct);
    const lowered = Object.entries(headers).find(
      ([k]) => String(k).toLowerCase() === String(key).toLowerCase()
    );
    return lowered ? String(lowered[1] || "") : "";
  }
  return "";
};

const stableBody = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return String(value);
  try {
    const sort = (input) => Array.isArray(input) ? input.map(sort) : input && typeof input === "object"
      ? Object.fromEntries(Object.keys(input).sort().map((key) => [key, sort(input[key])])) : input;
    return JSON.stringify(sort(JSON.parse(value)));
  } catch (_error) {
    return value;
  }
};

const normalizeUrl = (value) => {
  try {
    const url = new URL(String(value), "http://tenant-key.local");
    const query = Array.from(url.searchParams.entries()).sort(([aKey, aValue], [bKey, bValue]) =>
      aKey.localeCompare(bKey) || aValue.localeCompare(bValue));
    const search = new URLSearchParams(query).toString();
    return `${url.origin}${url.pathname}${search ? `?${search}` : ""}`;
  } catch (_error) {
    return String(value || "").trim();
  }
};

const resolveTenantFacts = (options = {}, config = {}) => {
  const headers = options?.headers;
  const auth = resolveHeaderValue(headers, "authorization");
  return {
    backend: String(config.backendIdentity || (() => { try { return new URL(String(config.url || ""),
      "http://tenant-key.local").origin; } catch (_error) { return "unknown"; } })()),
    school: String(config.schoolCode || resolveHeaderValue(headers, "x-school-code") ||
      activeTenantFacts.schoolCode || "none").trim().toLowerCase(),
    principal: String(config.principalId || activeTenantFacts.principalId || auth || "anonymous").trim(),
    student: String(config.activeStudentId || resolveHeaderValue(headers, "x-active-student-id") ||
      activeTenantFacts.activeStudentId || "none").trim(),
  };
};

export const buildTenantRequestKey = (url, options = {}, config = {}) => {
  const method = String(options?.method || "GET").toUpperCase();
  const facts = resolveTenantFacts(options, { ...config, url });
  return [TENANT_CONTRACT_VERSION, facts.backend, facts.school, facts.principal, facts.student, method,
    normalizeUrl(url), stableBody(options?.body), String(config.cacheKey || "")].join("|");
};

export const clearTenantRequestState = () => {
  responseCache.clear();
  inFlightRequests.clear();
  tenantGeneration += 1;
  activeTenantFingerprint = "";
  activeTenantFacts = {};
};

export const setActiveTenantRequestContext = ({ schoolCode, principalId, activeStudentId } = {}) => {
  const next = [String(schoolCode || "none").toLowerCase(), String(principalId || "anonymous"),
    String(activeStudentId || "none")].join("|");
  if (activeTenantFingerprint && activeTenantFingerprint !== next) clearTenantRequestState();
  activeTenantFingerprint = next;
  activeTenantFacts = { schoolCode: String(schoolCode || ""), principalId: String(principalId || ""),
    activeStudentId: String(activeStudentId || "") };
  return next;
};

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const resolveErrorMessage = (payload, fallback = "Request failed") => {
  if (!payload || typeof payload !== "object") return fallback;
  return (
    payload.message ||
    payload.msg ||
    payload.error ||
    payload?.errors?.[0]?.msg ||
    payload?.errors?.[0]?.message ||
    fallback
  );
};

export const fetchJson = async (url, options = {}, config = {}) => {
  const canonicalOptions = { ...options, headers: buildCanonicalAuthHeaders(options.headers) };
  const method = String(canonicalOptions?.method || "GET").toUpperCase();
  const cacheTtlMs = Number.isFinite(config?.cacheTtlMs) ? config.cacheTtlMs : 0;
  const dedupe = config?.dedupe !== false;
  const throwOnError = Boolean(config?.throwOnError);
  const disableSharing = Boolean(canonicalOptions?.signal);

  const cacheKey = disableSharing ? "" : buildTenantRequestKey(url, canonicalOptions, config);
  const requestGeneration = tenantGeneration;

  if (!disableSharing && method === "GET" && cacheTtlMs > 0 && cacheKey) {
    const cached = responseCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return { ok: true, status: 200, data: cached.data, error: "" };
    }
  }

  if (!disableSharing && dedupe && method === "GET" && cacheKey && inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const requestPromise = (async () => {
    try {
      const response = await fetch(url, canonicalOptions);
      const payload = await parseJsonSafe(response);
      const ok = Boolean(response.ok);
      const status = Number(response.status) || 0;
      const error = ok ? "" : resolveErrorMessage(payload, `Request failed (${status || "?"})`);

      if (requestGeneration !== tenantGeneration) return { ok: false, status: 409, data: null,
        error: "Tenant context changed while the request was in flight.", staleTenantContext: true };

      if (!ok) {
        if ([401, 403].includes(status)) clearTenantRequestState();
        if (throwOnError) {
          throw new Error(error);
        }
        return { ok, status, data: payload, error };
      }

      if (!disableSharing && method === "GET" && cacheTtlMs > 0 && cacheKey) {
        responseCache.set(cacheKey, { expiresAt: Date.now() + cacheTtlMs, data: payload });
      }

      return { ok, status, data: payload, error: "" };
    } catch (error) {
      const message = error?.message || "Network error";
      if (throwOnError) {
        throw error;
      }
      return { ok: false, status: 0, data: null, error: message };
    } finally {
      if (!disableSharing && cacheKey) {
        inFlightRequests.delete(cacheKey);
      }
    }
  })();

  if (!disableSharing && cacheKey) {
    inFlightRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
};
